import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfiguration } from "../../../../../hooks/useConfiguration";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { GuestCountModal } from "./GuestCountModal";

/** Началният брой, когато сметката още няма въведен. */
const DEFAULT_GUESTS = 2;

interface PendingAsk {
  initialCount: number;
  /** Записът в BE (при закриване) — модалът се върти, докато не мине. */
  save?: (guests: number) => Promise<void>;
  resolve: (guests: number | null) => void;
}

/**
 * Питането „Брой гости" (настройка „Въвеждане на брой гости", Конфигурация →
 * Маркиране). `askGuestCount` връща обещание: броят при „Потвърди", `null`
 * при „Отказ" — тогава извикващият НЕ отваря следващия модал („Нова маса" /
 * „Закрий"). С `save` броят се записва още докато модалът е отворен; провален
 * запис е предупреждение, не спира закриването (при съмнение обслужвай).
 *
 * `askFor` казва дали за този момент изобщо се пита: настройката съвпада и
 * НЕ сме в локален режим — касата няма къде да запише броя
 * (docs/ordering.md §Капани). Модалът (`guestCountModal`) се рендерира от
 * извикващия.
 */
export const useGuestCountPrompt = () => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { configuration } = useConfiguration();
  const edgeLocal = useIsEdgeLocal();
  const [pending, setPending] = useState<PendingAsk | null>(null);
  const [saving, setSaving] = useState(false);
  const pendingRef = useRef<PendingAsk | null>(null);
  pendingRef.current = pending;

  // Затворен екран с висящо питане = „Отказ" (обещанието не остава да виси).
  useEffect(() => () => pendingRef.current?.resolve(null), []);

  const askFor = (moment: "new_table" | "close") =>
    !edgeLocal && configuration.orderingGuestCount === moment;

  const askGuestCount = useCallback(
    (options?: {
      initialCount?: number | null;
      save?: (guests: number) => Promise<void>;
    }) =>
      new Promise<number | null>((resolve) => {
        // Второ питане (напр. F5 два пъти) отменя първото.
        pendingRef.current?.resolve(null);
        setPending({
          initialCount: options?.initialCount ?? DEFAULT_GUESTS,
          save: options?.save,
          resolve,
        });
      }),
    [],
  );

  const handleCancel = () => {
    pending?.resolve(null);
    setPending(null);
  };

  const handleConfirm = async (guests: number) => {
    if (!pending) return;
    if (pending.save) {
      setSaving(true);
      try {
        await pending.save(guests);
      } catch (err) {
        message.warning(
          `${t("ordering.guestCount.saveError")}: ${getApiErrorMessage(err, t("common.error"))}`,
        );
      } finally {
        setSaving(false);
      }
    }
    pending.resolve(guests);
    setPending(null);
  };

  const guestCountModal = (
    <GuestCountModal
      open={pending !== null}
      initialCount={pending?.initialCount ?? DEFAULT_GUESTS}
      saving={saving}
      onCancel={handleCancel}
      onConfirm={(guests) => void handleConfirm(guests)}
    />
  );

  return {
    askFor,
    askGuestCount,
    guestCountModal,
    guestCountModalOpen: pending !== null,
  };
};
